import { APIClientServices } from "./APIClientServices";
import { UserServices } from "./UserServices";
import { WSServices } from "./WSServices";
import type { LegacyAssistantMessageDTO, PublicMessageDTO } from "shared/types/MessageContracts";

// DTOs baseados na API
export interface AssistantConversationDTO {
	id: string;
	userId: string;
	title: string | null;
	isArchived: boolean;
	createdAt: string;
	updatedAt: string;
	messagesCount?: number;
}

export type AssistantMessageDTO = PublicMessageDTO | LegacyAssistantMessageDTO;

export interface AssistantConversationWithMessagesDTO {
	conversation: AssistantConversationDTO;
	messages: AssistantMessageDTO[];
	total: number;
}

/**
 * EK-408: anexo aceita 2 formatos:
 *  - persistido (preferencial): { attachmentId } — referência ao registro
 *    em chat_attachments criado via uploadChatAttachment.
 *  - inline (legado): { filename, fileData, mimeType } base64 data URL.
 */
export type MessageAttachment =
	| { attachmentId: string; filename?: string; mimeType?: string }
	| { filename: string; fileData: string; mimeType: string };

export interface ChatAttachmentDTO {
	id: string;
	workspaceId: string;
	accountId: string;
	conversationId: string | null;
	messageId: string | null;
	filename: string;
	mimeType: string;
	sizeBytes: number;
	storageUrl: string;
	createdAt: string;
}

export interface SendMessageResult {
	userMessage: AssistantMessageDTO;
}

export interface ProcessingStatusDTO {
	conversationId: string;
	status: "idle" | "queued" | "processing" | "waiting_confirmation" | "executing_tool" | "completed" | "error";
	currentStep?: string;
	toolsExecuted?: Array<{ name: string; id: string }>;
	tokensGenerated?: number;
	error?: string;
	result?: {
		userMessage: AssistantMessageDTO;
		assistantMessage: AssistantMessageDTO;
	};
}

// Tipos de eventos SSE
export interface SSEProcessingStarted {
	type: "message_started";
	userMessage: AssistantMessageDTO | AssistantMessageDTO[];
}

export interface SSEToken {
	type: "token";
	token: string;
	content?: string;
}

export interface SSEToolCall {
	type: "tool_call";
	name: string;
	id: string;
	arguments?: Record<string, any>;
}

export interface SSEToolResult {
	type: "tool_result";
	name: string;
	id: string;
	result?: any;
}

export interface SSEConfirmationRequired {
	type: "confirmation_required";
	confirmationId: string;
	toolName: string;
	description: string;
	arguments?: Record<string, any>;
	toolCallId?: string;
	userId?: string;
}

export interface SSEConfirmationResolved {
	type: "confirmation_resolved";
	confirmationId: string;
	approved: boolean;
}

export interface SSEConfirmationPending {
	type: "confirmation_pending";
	confirmationId: string;
	request?: Record<string, any>;
	description?: string;
}

export interface SSEConfirmationMessage {
	type: "confirmation_message";
	conversationId: string;
	message: AssistantMessageDTO;
}

export interface SSEMessageSaved {
	type: "message_saved";
	userMessage?: AssistantMessageDTO;
	assistantMessage?: AssistantMessageDTO;
	leaderMessage?: AssistantMessageDTO;
}

export interface SSEComplete {
	type: "complete";
	content: string;
}

export interface SSEDone {
	type: "done";
}

export interface SSEError {
	type: "error";
	message: string;
	code?: string;
}

export type SSEEvent =
	| SSEProcessingStarted
	| SSEToken
	| SSEToolCall
	| SSEToolResult
	| SSEConfirmationRequired
	| SSEConfirmationPending
	| SSEConfirmationResolved
	| SSEMessageSaved
	| SSEComplete
	| SSEDone
	| SSEError;

export interface StreamCallbacks {
	onProcessingStarted?: (data: SSEProcessingStarted) => void;
	onToken?: (data: SSEToken) => void;
	onToolCall?: (data: SSEToolCall) => void;
	onToolResult?: (data: SSEToolResult) => void;
	onConfirmationRequired?: (data: SSEConfirmationRequired) => void;
	onConfirmationPending?: (data: SSEConfirmationPending) => void;
	onConfirmationResolved?: (data: SSEConfirmationResolved) => void;
	onConfirmationMessage?: (data: SSEConfirmationMessage) => void;
	onMessageSaved?: (data: SSEMessageSaved) => void;
	onComplete?: (data: SSEComplete) => void;
	onDone?: (data: SSEDone) => void;
	onError?: (data: SSEError) => void;
	onProcessingStatus?: (data: WSProcessingStatus) => void;
	onProcessingCompleted?: (data: WSProcessingCompleted) => void;
	onProcessingError?: (data: WSProcessingError) => void;
}

/**
 * Tipos de eventos WebSocket emitidos pelo servidor.
 * O servidor emite via `WebsocketService.sendBy(userId, "personal:conversation:{id}:stream", data)`
 * que chega no cliente via `socket.on("receive", { event: "personal:conversation:{id}:stream", data })`.
 */
export interface WSProcessingStatus {
	type: "message_status";
	conversationId: string;
	status: string;
	currentStep?: string;
	toolsExecuted?: Array<{ name: string; id: string }>;
	tokensGenerated?: number;
}

export interface WSProcessingCompleted {
	type: "message_completed";
	conversationId: string;
}

export interface WSProcessingError {
	type: "message_error";
	conversationId: string;
	error: string;
}

const API_URL = APIClientServices.API_URL;

function resolveUrl(base: string, path: string): string {
	try {
		new URL(path);
		return path;
	} catch {}
	const baseUrl = new URL(base);
	if (path.startsWith("/")) {
		baseUrl.pathname = path;
		return baseUrl.toString();
	}
	const basePath = baseUrl.pathname;
	const finalPath = basePath.endsWith("/") ? basePath + path : basePath + "/" + path;
	baseUrl.pathname = finalPath;
	return baseUrl.toString();
}

export class AssistantServices {
	private static readonly BASE_ROUTE = "v1/assistant";

	private static route(workspaceId?: string | null) {
		if (workspaceId) {
			return `${this.BASE_ROUTE}/${workspaceId}`;
		}

		return this.BASE_ROUTE;
	}

	/**
	 * Cria uma nova conversa com o Assistente.
	 */
	static async createConversation(workspaceId?: string | null, title?: string): Promise<AssistantConversationDTO> {
		return await APIClientServices.post<AssistantConversationDTO>(`${this.route(workspaceId)}/conversations`, title ? { title } : undefined);
	}

	/**
	 * Lista todas as conversas do usuário com o Assistente.
	 */
	static async listConversations(workspaceId?: string | null): Promise<AssistantConversationDTO[]> {
		return await APIClientServices.get<AssistantConversationDTO[]>(`${this.route(workspaceId)}/conversations`);
	}

	/**
	 * Obtém uma conversa específica com o histórico de mensagens.
	 */
	static async getConversation(
		workspaceId: string | null | undefined,
		conversationId: string,
		limit = 50,
		offset = 0,
	): Promise<AssistantConversationWithMessagesDTO> {
		return await APIClientServices.get<AssistantConversationWithMessagesDTO>(`${this.route(workspaceId)}/conversations/${conversationId}`, {
			limit: limit.toString(),
			offset: offset.toString(),
		});
	}

	/**
	 * Envia uma mensagem em background (202 Accepted).
	 */
	static async sendMessage(workspaceId: string | null | undefined, conversationId: string, content: string, attachments?: MessageAttachment[]): Promise<SendMessageResult> {
		// EK-404 #2: gera 1 chave por chamada. Se a UI re-enviar (StrictMode,
		// retry de rede, click duplo), a mesma chave faz o backend retornar a
		// mensagem original em vez de duplicar.
		const idempotencyKey = (typeof crypto !== "undefined" && (crypto as any).randomUUID)
			? (crypto as any).randomUUID()
			: `${Date.now()}-${Math.random().toString(36).slice(2)}`;
		return await APIClientServices.post<SendMessageResult>(
			`${this.route(workspaceId)}/conversations/${conversationId}/messages`,
			{ content, ...(attachments?.length ? { attachments } : {}) },
			undefined,
			{ "Idempotency-Key": idempotencyKey },
		);
	}

	/**
	 * Envia uma mensagem com streaming SSE.
	 * Retorna um AbortController para cancelar o stream.
	 */
	static sendMessageStream(
		workspaceId: string | null | undefined,
		conversationId: string,
		content: string,
		callbacks: StreamCallbacks,
		attachments?: MessageAttachment[],
	): AbortController {
		const controller = new AbortController();

		const url = resolveUrl(API_URL, `${this.route(workspaceId)}/conversations/${conversationId}/messages/stream`);

		const execute = async () => {
			let doneSignaled = false;

			const signalDone = () => {
				if (doneSignaled) return;
				doneSignaled = true;
				callbacks.onDone?.({ type: "done" });
			};

			const routeEvent = (eventType: string, data: any) => {
				switch (eventType) {
					case "message_started":
						callbacks.onProcessingStarted?.(data);
						break;
					case "token":
						callbacks.onToken?.({
							type: "token",
							token: data?.token ?? "",
							content: data?.content,
						});
						break;
					case "tool_call":
						callbacks.onToolCall?.(data);
						break;
					case "tool_result":
						callbacks.onToolResult?.(data);
						break;
					case "confirmation_required":
						callbacks.onConfirmationRequired?.(data);
						break;
					case "confirmation_pending":
						callbacks.onConfirmationPending?.(data);
						break;
					case "confirmation_resolved":
						callbacks.onConfirmationResolved?.(data);
						break;
					case "message_saved":
						callbacks.onMessageSaved?.(data);
						break;
					case "complete":
						callbacks.onComplete?.(data);
						break;
					case "done":
						signalDone();
						break;
					case "message_error":
						callbacks.onProcessingError?.(data);
						callbacks.onError?.({ type: "error", message: data?.error || "Erro no processamento" });
						break;
					case "error":
						callbacks.onError?.({
							type: "error",
							message: data?.message || "Erro inesperado durante o streaming",
						});
						break;
				}
			};

			const parseFrame = (frame: string) => {
				if (!frame.trim()) return;

				let eventName = "";
				const dataLines: string[] = [];

				for (const rawLine of frame.split("\n")) {
					const line = rawLine.trimEnd();
					if (!line || line.startsWith(":")) continue;
					if (line.startsWith("event:")) {
						eventName = line.slice(6).trim();
						continue;
					}
					if (line.startsWith("data:")) {
						dataLines.push(line.slice(5).trimStart());
					}
				}

				if (dataLines.length === 0) return;

				const rawData = dataLines.join("\n");
				let parsedData: any = null;

				try {
					parsedData = JSON.parse(rawData);
				} catch {
					return;
				}

				const inferredType = eventName || parsedData?.type || (parsedData?.token ? "token" : "");
				if (!inferredType) return;

				routeEvent(inferredType, parsedData);
			};

			try {
				const headers: Record<string, string> = {
					"Content-Type": "application/json",
					"Accept": "text/event-stream",
					"X-Requested-From": window.location.href,
				};

				if (UserServices.currentToken) {
					headers["Authorization"] = `Bearer ${UserServices.currentToken}`;
				}

				const response = await fetch(url, {
					method: "POST",
					headers,
					body: JSON.stringify({ content, ...(attachments?.length ? { attachments } : {}) }),
					signal: controller.signal,
				});

				if (!response.ok) {
					const errorText = await response.text();
					let errorMessage = errorText;
					try {
						const errorJson = JSON.parse(errorText);
						errorMessage = errorJson.message || errorText;
					} catch {}
					callbacks.onError?.({ type: "error", message: errorMessage });
					return;
				}

				const responseContentType = response.headers.get("content-type") || "";
				if (!responseContentType.includes("text/event-stream")) {
					callbacks.onError?.({
						type: "error",
						message: "Resposta inválida do servidor para streaming SSE",
					});
					return;
				}

				const reader = response.body?.getReader();
				if (!reader) {
					callbacks.onError?.({ type: "error", message: "Stream não disponível" });
					return;
				}

				const decoder = new TextDecoder();
				let buffer = "";

				while (true) {
					const { done, value } = await reader.read();
					if (done) break;
					if (!value) continue;

					buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");

					const frames = buffer.split("\n\n");
					buffer = frames.pop() || "";

					for (const frame of frames) {
						parseFrame(frame);
					}
				}

				buffer += decoder.decode();
				if (buffer.trim()) {
					parseFrame(buffer);
				}

				signalDone();
			} catch (error: any) {
				if (error.name !== "AbortError") {
					callbacks.onError?.({ type: "error", message: error?.message ?? "Erro inesperado durante o streaming" });
				}
			}
		};

		execute();
		return controller;
	}

	/**
	 * Obtém o status de processamento de uma conversa.
	 */
	static async getProcessingStatus(workspaceId: string | null | undefined, conversationId: string): Promise<ProcessingStatusDTO> {
		return await APIClientServices.get<ProcessingStatusDTO>(`${this.route(workspaceId)}/conversations/${conversationId}/processing`);
	}

	/**
	 * Confirma ou nega a execução de uma ferramenta.
	 */
	static async confirmToolExecution(
		workspaceId: string | null | undefined,
		conversationId: string,
		confirmationId: string,
		approved: boolean,
		reason?: string,
	): Promise<{ success: boolean; confirmationId: string; approved: boolean }> {
		return await APIClientServices.post(`${this.route(workspaceId)}/confirmations/${conversationId}/${confirmationId}`, {
			approved,
			reason,
		});
	}

	// ────────────────────────────────────────────────────────────────
	// WebSocket — Assinatura de eventos em tempo real
	// ────────────────────────────────────────────────────────────────

	/**
	 * Gera o nome do evento WebSocket para streaming de uma conversa.
	 * Formato: `personal:conversation:{id}:stream`
	 */
	static getStreamEventName(conversationId: string): string {
		return `personal:conversation:${conversationId}:stream`;
	}

	/**
	 * Inscreve-se nos eventos WebSocket de uma conversa específica.
	 * O servidor emite eventos via `sendBy(userId, "personal:conversation:{id}:stream", data)`.
	 * No cliente, chegam como `receive` events filtrados pelo nome do evento.
	 *
	 * @param conversationId - ID da conversa a escutar
	 * @param callbacks - Callbacks para cada tipo de evento
	 * @returns Objeto com método `stop()` para cancelar a assinatura
	 */
	static subscribeToConversation(conversationId: string, callbacks: StreamCallbacks): { stop: () => void } {
		const eventName = this.getStreamEventName(conversationId);

		const handler = (data: any) => {
			if (!data || !data.type) return;

			switch (data.type) {
				case "message_started":
					callbacks.onProcessingStarted?.(data);
					break;
				case "token":
					callbacks.onToken?.(data);
					break;
				case "tool_call":
					callbacks.onToolCall?.({
						type: "tool_call",
						name: data.toolCall?.name ?? data.name,
						id: data.toolCall?.id ?? data.id,
						arguments: data.toolCall?.arguments ?? data.arguments,
					});
					break;
				case "tool_result":
					callbacks.onToolResult?.({
						type: "tool_result",
						name: data.functionName ?? data.name,
						id: data.toolCallId ?? data.id,
						result: data.result,
					});
					break;
				case "confirmation_required":
					callbacks.onConfirmationRequired?.({
						type: "confirmation_required",
						confirmationId: data.confirmation?.confirmationId ?? data.confirmationId,
						toolName:
							data.confirmation?.toolName ?? data.toolName ?? data.confirmation?.functionName ?? data.functionName ?? "unknown_tool",
						description: data.confirmation?.description ?? data.description,
						arguments: data.confirmation?.arguments ?? data.arguments,
						toolCallId: data.confirmation?.toolCallId ?? data.toolCallId,
						userId: data.confirmation?.userId ?? data.userId,
					});
					break;
				case "confirmation_pending":
					callbacks.onConfirmationPending?.({
						type: "confirmation_pending",
						confirmationId: data.confirmationId,
						description: data.description,
						request: data.request,
					});
					break;
				case "confirmation_resolved":
					callbacks.onConfirmationResolved?.(data);
					break;
				case "confirmation_message":
					callbacks.onConfirmationMessage?.(data);
					break;
				case "complete":
					callbacks.onComplete?.(data);
					break;
				case "message_saved":
					callbacks.onMessageSaved?.(data);
					break;
				case "message_status":
					callbacks.onProcessingStatus?.(data);
					break;
				case "message_completed":
					callbacks.onProcessingCompleted?.(data);
					callbacks.onDone?.({ type: "done" });
					break;
				case "message_error":
					callbacks.onProcessingError?.(data);
					callbacks.onError?.({ type: "error", message: data.error || "Erro no processamento" });
					break;
				case "error":
					callbacks.onError?.(data);
					break;
			}
		};

		const subscription = WSServices.onReceive(eventName, handler);

		return {
			stop: () => {
				subscription.stop();
			},
		};
	}

	/**
	 * Envia uma mensagem em background (202) e automaticamente
	 * assina os eventos WebSocket para receber atualizações em tempo real.
	 *
	 * @param workspaceId - ID do workspace
	 * @param conversationId - ID da conversa
	 * @param content - Conteúdo da mensagem
	 * @param callbacks - Callbacks para eventos de streaming
	 * @returns Objeto com resultado da mensagem e método `stop()` para encerrar a assinatura WS
	 */
	static async sendMessageWithWS(
		workspaceId: string | null | undefined,
		conversationId: string,
		content: string,
		callbacks: StreamCallbacks,
		attachments?: MessageAttachment[],
	): Promise<{ result: SendMessageResult; stop: () => void }> {
		// Inscreve-se ANTES de enviar a mensagem para não perder eventos
		const subscription = this.subscribeToConversation(conversationId, callbacks);

		try {
			const result = await this.sendMessage(workspaceId, conversationId, content, attachments);
			return { result, stop: subscription.stop };
		} catch (error: any) {
			subscription.stop();
			throw error;
		}
	}
}
